import * as React from 'react';
import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';
import { Circle } from 'lucide-react';
import { cn } from '@utils/cn';

/**
 * RadioGroup Root Component
 */
const RadioGroup = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Root>
>(({ className, ...props }, ref) => {
  return (
    <RadioGroupPrimitive.Root
      className={cn('grid gap-2', className)}
      {...props}
      ref={ref}
    />
  );
});
RadioGroup.displayName = RadioGroupPrimitive.Root.displayName;

/**
 * Radio Item Component
 */
const RadioGroupItem = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item>
>(({ className, ...props }, ref) => {
  return (
    <RadioGroupPrimitive.Item
      ref={ref}
      className={cn(
        'aspect-square h-5 w-5 rounded-full border border-primary text-primary ring-offset-background focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
        className,
      )}
      {...props}
    >
      <RadioGroupPrimitive.Indicator className="flex items-center justify-center">
        <Circle className="h-2.5 w-2.5 fill-current text-current" />
      </RadioGroupPrimitive.Indicator>
    </RadioGroupPrimitive.Item>
  );
});
RadioGroupItem.displayName = RadioGroupPrimitive.Item.displayName;

/**
 * Radio Option Props
 */
export interface RadioOption {
  value: string;
  label: string;
  description?: string;
  disabled?: boolean;
}

/**
 * Radio Field Props
 */
export interface RadioFieldProps {
  /**
   * Radio options
   */
  options: RadioOption[];
  /**
   * Currently selected value
   */
  value?: string;
  /**
   * Change handler
   */
  onValueChange?: (value: string) => void;
  /**
   * Label for the radio group
   */
  label?: string;
  /**
   * Description for the radio group
   */
  description?: string;
  /**
   * Error message
   */
  error?: string;
  /**
   * Helper text
   */
  helperText?: string;
  /**
   * Orientation of radio buttons
   */
  orientation?: 'vertical' | 'horizontal';
  /**
   * Disabled state for all radio buttons
   */
  disabled?: boolean;
  /**
   * Wrapper className
   */
  wrapperClassName?: string;
  /**
   * RadioGroup className
   */
  className?: string;
  /**
   * Name attribute for the radio group (for forms)
   */
  name?: string;
  /**
   * Required field indicator
   */
  required?: boolean;
}

/**
 * RadioField Component
 *
 * A radio group component with label, description, and error states.
 * Provides a clean interface for single-choice selection.
 *
 * @example
 * // Basic radio group
 * const options = [
 *   { value: 'male', label: 'Male' },
 *   { value: 'female', label: 'Female' },
 *   { value: 'other', label: 'Other' },
 * ];
 * <RadioField
 *   label="Gender"
 *   options={options}
 *   value={gender}
 *   onValueChange={setGender}
 * />
 *
 * @example
 * // Radio group with descriptions
 * const urgencyOptions = [
 *   {
 *     value: 'low',
 *     label: 'Low Priority',
 *     description: 'Non-urgent, can wait',
 *   },
 *   {
 *     value: 'medium',
 *     label: 'Medium Priority',
 *     description: 'Should be addressed soon',
 *   },
 *   {
 *     value: 'high',
 *     label: 'High Priority',
 *     description: 'Requires immediate attention',
 *   },
 * ];
 * <RadioField
 *   label="Urgency Level"
 *   options={urgencyOptions}
 * />
 *
 * @example
 * // Horizontal layout
 * <RadioField
 *   label="Yes or No"
 *   options={[
 *     { value: 'yes', label: 'Yes' },
 *     { value: 'no', label: 'No' },
 *   ]}
 *   orientation="horizontal"
 * />
 *
 * @example
 * // With React Hook Form
 * const { register, formState: { errors } } = useForm();
 * <RadioField
 *   {...register('bloodType', { required: 'Blood type is required' })}
 *   label="Blood Type"
 *   options={bloodTypeOptions}
 *   error={errors.bloodType?.message}
 * />
 */
const RadioField = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Root>,
  RadioFieldProps
>(
  (
    {
      options,
      value,
      onValueChange,
      label,
      description,
      error,
      helperText,
      orientation = 'vertical',
      disabled,
      wrapperClassName,
      className,
      name,
      required,
      ...props
    },
    ref,
  ) => {
    const id = React.useId();
    const errorId = `${id}-error`;
    const helperId = `${id}-helper`;
    const descriptionId = `${id}-description`;

    return (
      <div className={cn('space-y-3', wrapperClassName)}>
        {(label || description) && (
          <div className="space-y-1">
            {label && (
              <div className="text-sm font-medium leading-none">
                {label}
                {required && (
                  <span className="ml-1 text-destructive" aria-label="required">
                    *
                  </span>
                )}
              </div>
            )}
            {description && (
              <p id={descriptionId} className="text-sm text-muted-foreground">
                {description}
              </p>
            )}
          </div>
        )}

        <RadioGroup
          ref={ref}
          value={value}
          onValueChange={onValueChange}
          disabled={disabled}
          name={name}
          className={cn(
            orientation === 'horizontal' && 'flex flex-row flex-wrap gap-4',
            className,
          )}
          aria-invalid={!!error}
          aria-describedby={
            error
              ? errorId
              : description
                ? descriptionId
                : helperText
                  ? helperId
                  : undefined
          }
          aria-required={required}
          {...props}
        >
          {options.map((option) => (
            <div
              key={option.value}
              className="flex items-start gap-2"
            >
              <RadioGroupItem
                value={option.value}
                id={`${id}-${option.value}`}
                disabled={option.disabled || disabled}
                className="mt-0.5"
              />
              <div className="grid gap-1.5 leading-none">
                <label
                  htmlFor={`${id}-${option.value}`}
                  className={cn(
                    'text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer',
                  )}
                >
                  {option.label}
                </label>
                {option.description && (
                  <p className="text-sm text-muted-foreground">
                    {option.description}
                  </p>
                )}
              </div>
            </div>
          ))}
        </RadioGroup>

        {(error || helperText) && (
          <div
            id={error ? errorId : helperId}
            className={cn('text-sm', {
              'text-destructive': error,
              'text-muted-foreground': helperText && !error,
            })}
            role={error ? 'alert' : 'status'}
            aria-live="polite"
          >
            {error || helperText}
          </div>
        )}
      </div>
    );
  },
);

RadioField.displayName = 'RadioField';

export { RadioGroup, RadioGroupItem, RadioField };
